import { Button, Drawer, Input, Prompt, Switch, Text, toast } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router-dom"
import { Field } from "@/components/form"
import { Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { errorMessage, sdk } from "@/lib/sdk"
import {
  useConnectShipStation,
  useDisconnectShipStation,
  useOrderShipStation,
  useResendToShipStation,
  useShipStation,
  useShipStationEnabled,
  useSyncShipStation,
  useTestShipStation,
  type OrderShipStation,
} from "@/lib/shipstation"
import { Badge, Banner, DefinitionList, type Tone } from "@/terabizz"
import { ShipStationShippingDefaults } from "./shipstation-shipping"
import { ShipStationStoreSetting } from "./shipstation-store"

// ── Settings → Shipping and delivery → ShipStation ─────────────────────────

/**
 * Connect ShipStation so every order is shipped from there. The key and secret are stored
 * encrypted and never shown again (last 4 only). Only the owner and staff with full
 * permissions may change it; everyone with settings access sees the status.
 */
export function ShipStationCard() {
  const { data, error } = useShipStation()
  const [connecting, setConnecting] = useState(false)
  const [confirmOff, setConfirmOff] = useState(false)
  const enabled = useShipStationEnabled()
  const test = useTestShipStation()
  const sync = useSyncShipStation()
  const disconnect = useDisconnectShipStation()
  if (error) return <Section title="ShipStation"><Text size="small" className="p-4 text-ui-fg-error">{errorMessage(error)}</Text></Section>
  const s = data?.shipstation
  if (!s) return null
  const manage = s.can_manage

  return (
    <Section
      title="ShipStation"
      actions={
        s.connected ? (
          <Badge tone={!s.readable ? "critical" : s.enabled ? "success" : "neutral"}>{!s.readable ? "Reconnect needed" : s.enabled ? "On" : "Off"}</Badge>
        ) : (
          <Badge tone="neutral">Not connected</Badge>
        )
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <Text size="small" className="text-ui-fg-subtle">
          New orders are sent to ShipStation automatically. When you buy a label there, the order is fulfilled here with the carrier and tracking number, and the customer gets the shipping confirmation email.
        </Text>
        {s.connected && !s.readable && (
          <Banner tone="critical" title="The saved ShipStation credentials can't be read">The server secret changed. Connect again with the API key and secret.</Banner>
        )}
        <FailedBanners s={s as StatusExtra} manage={manage} />
      </div>
      {s.connected && (
        <DefinitionList
          rows={[
            ["API", s.api_label ?? s.api ?? "—"],
            ["API key", s.key_hint ?? "—"],
            ...(s.api === "v1" ? ([["API secret", s.has_secret ? "Saved (hidden)" : "—"]] as [string, string][]) : []),
            ["Shipment updates", s.webhook?.subscribed ? "Webhook set up automatically, plus a check every 15 minutes" : "Checked every 15 minutes (add the webhook below for instant updates)"],
            ["Last check for shipments", s.last_poll_at ? formatDate(s.last_poll_at) : "Not yet"],
            ["Connected", s.connected_at ? formatDate(s.connected_at) : "—"],
            ...(s.last_test ? ([["Last connection test", `${s.last_test.ok ? "OK" : "Failed"} · ${formatDate(s.last_test.at)}`]] as [string, string][]) : []),
          ]}
        />
      )}
      {s.connected && s.webhook?.url && !s.webhook.subscribed && (
        <div className="px-4 pb-3">
          <Field label="Webhook URL (paste in ShipStation → Settings → Integrations → Webhooks, event “On Orders Shipped”)">
            <Input readOnly value={s.webhook.url} onFocus={(e) => e.currentTarget.select()} />
          </Field>
        </div>
      )}
      {s.connected && s.readable && <ShipStationStoreSetting manage={manage} />}
      {s.connected && s.readable && <ShipStationShippingDefaults manage={manage} />}
      {manage ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ui-border-base px-4 py-3">
          {s.connected ? (
            <>
              <label className="flex items-center gap-2 text-[13px]">
                <Switch
                  checked={s.enabled}
                  disabled={enabled.isPending || !s.readable}
                  onCheckedChange={(v) => (v ? enabled.mutate(true, { onSuccess: () => toast.success("ShipStation switched on"), onError: (e) => toast.error(errorMessage(e)) }) : setConfirmOff(true))}
                />
                Send orders and receive shipments
              </label>
              <div className="flex flex-wrap gap-2">
                <Button size="small" variant="secondary" isLoading={test.isPending} onClick={() => test.mutate(undefined, { onSuccess: (r: any) => (r.test.ok ? toast.success(r.test.message) : toast.error(r.test.message || "ShipStation didn't accept the test. Try again in a minute.")), onError: (e) => toast.error(errorMessage(e)) })}>
                  Test connection
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  isLoading={sync.isPending}
                  disabled={!s.enabled}
                  onClick={() =>
                    sync.mutate(undefined, {
                      onSuccess: (r: any) => (r.sync.shipments.error ? toast.error(r.sync.shipments.error) : toast.success(`Checked ShipStation: ${r.sync.shipments.fulfilled ?? 0} new shipment${r.sync.shipments.fulfilled === 1 ? "" : "s"}, ${r.sync.sent.sent ?? 0} order${r.sync.sent.sent === 1 ? "" : "s"} sent`)),
                      onError: (e) => toast.error(errorMessage(e)),
                    })
                  }
                >
                  Sync now
                </Button>
                <Button size="small" variant="secondary" onClick={() => setConnecting(true)}>Change keys</Button>
                <Button size="small" variant="danger" isLoading={disconnect.isPending} onClick={() => disconnect.mutate(undefined, { onSuccess: () => toast.success("ShipStation disconnected"), onError: (e) => toast.error(errorMessage(e)) })}>
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <Button size="small" onClick={() => setConnecting(true)}>Connect ShipStation</Button>
          )}
        </div>
      ) : (
        <Text size="xsmall" className="px-4 pb-4 text-ui-fg-subtle">Only store owners and staff with full permissions can connect or change ShipStation.</Text>
      )}
      {connecting && <ConnectDrawer onClose={() => setConnecting(false)} />}
      <Prompt open={confirmOff} onOpenChange={setConfirmOff}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Switch ShipStation off?</Prompt.Title>
            <Prompt.Description>New orders won't be sent and shipments made in ShipStation won't be recorded here until you switch it back on. You can send missed orders from each order page.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep on</Prompt.Cancel>
            <Prompt.Action onClick={() => enabled.mutate(false, { onSuccess: () => toast.success("ShipStation switched off"), onError: (e) => toast.error(errorMessage(e)) })}>Switch off</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Section>
  )
}

/** Extra fields of GET /admin/terify/settings/shipstation used by the banners (lib/shipstation/settings.ts shipStationStatus). */
type ProblemOrder = { order_id: string; name: string; last_error: string | null; updated_at: string }
type StatusExtra = {
  enabled: boolean
  counts?: { failed?: number; on_hold?: number; shipments_failed?: number }
  failed_orders?: ProblemOrder[]
  on_hold_orders?: ProblemOrder[]
}

const OrderLinks = ({ list, total }: { list: ProblemOrder[]; total: number }) => (
  <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[13px]">
    {list.slice(0, 25).map((o) => (
      <Link key={o.order_id} to={`/orders/${o.order_id}`} title={o.last_error ?? undefined} className="text-ui-fg-interactive hover:underline">
        {o.name}
      </Link>
    ))}
    {total > Math.min(list.length, 25) && <span className="text-ui-fg-subtle">and {total - Math.min(list.length, 25)} more</span>}
  </div>
)

/** Failed sends (with "Retry all"), shipments that couldn't be recorded, and orders on hold for payment review. */
function FailedBanners({ s, manage }: { s: StatusExtra; manage: boolean }) {
  const qc = useQueryClient()
  const retry = useMutation({
    mutationFn: () => sdk.client.fetch<{ retry: { failed: number; queued: number; sent: Record<string, number>; shipments: Record<string, number> & { error?: string } }; shipstation: unknown }>("/admin/terify/settings/shipstation/retry-failed", { method: "POST", body: {} }),
    onSuccess: (r) => {
      if (r?.shipstation) qc.setQueryData(["settings", "shipstation"], { shipstation: r.shipstation })
      else qc.invalidateQueries({ queryKey: ["settings", "shipstation"] })
      const sent = r.retry.sent?.sent ?? 0
      const failed = r.retry.sent?.failed ?? 0
      const held = r.retry.sent?.held ?? 0
      const msg = `Retried ${r.retry.queued} order${r.retry.queued === 1 ? "" : "s"}: ${sent} sent${failed ? `, ${failed} failed again` : ""}${held ? `, ${held} on hold` : ""}. ${r.retry.shipments?.fulfilled ?? 0} shipment${r.retry.shipments?.fulfilled === 1 ? "" : "s"} recorded.`
      if (failed || r.retry.shipments?.error) toast.warning(msg)
      else toast.success(msg)
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const failed = s.counts?.failed ?? 0
  const held = s.counts?.on_hold ?? 0
  const shipments = s.counts?.shipments_failed ?? 0
  return (
    <>
      {(failed > 0 || shipments > 0) && (
        <Banner
          tone="warning"
          title={
            failed > 0
              ? `${failed} order${failed === 1 ? "" : "s"} couldn't be sent to ShipStation${shipments ? `, and ${shipments} shipment${shipments === 1 ? "" : "s"} couldn't be recorded` : ""}`
              : `${shipments} ShipStation shipment${shipments === 1 ? "" : "s"} couldn't be recorded here`
          }
        >
          <div className="flex flex-col gap-2">
            <span>Failed sends are retried automatically for about a day and shipments on every check; open an order to see why. Admin → Issues lists each one.</span>
            {!!s.failed_orders?.length && <OrderLinks list={s.failed_orders} total={failed} />}
            {manage && s.enabled && (
              <div>
                <Button size="small" variant="secondary" isLoading={retry.isPending} onClick={() => retry.mutate()}>Retry all</Button>
              </div>
            )}
          </div>
        </Banner>
      )}
      {held > 0 && (
        <Banner tone="warning" title={`${held} order${held === 1 ? " is" : "s are"} on hold and not sent to ShipStation: payment held for review`}>
          <div className="flex flex-col gap-2">
            <span>Approve the payment in Authorize.net and capture it on the order (or cancel the order). Held orders are checked every 5 minutes and sent once the payment clears.</span>
            {!!s.on_hold_orders?.length && <OrderLinks list={s.on_hold_orders} total={held} />}
          </div>
        </Banner>
      )}
    </>
  )
}

function ConnectDrawer({ onClose }: { onClose: () => void }) {
  const connect = useConnectShipStation()
  const [key, setKey] = useState("")
  const [secret, setSecret] = useState("")
  const submit = () =>
    connect.mutate(
      { api_key: key.trim(), api_secret: secret.trim() || null },
      {
        onSuccess: (r: any) => {
          toast.success("ShipStation connected")
          if (r.webhook_error) toast.warning(`Shipments will be checked every 15 minutes: the webhook couldn't be set up (${r.webhook_error}).`)
          onClose()
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    )
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>Connect ShipStation</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-4 overflow-y-auto">
          <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-[13px] text-ui-fg-subtle">
            <li>In ShipStation go to <b>Settings → Account → API Settings</b>.</li>
            <li>Under <b>Select API Version</b> choose <b>V1 API</b>.</li>
            <li>Click <b>Generate API Key</b>. Make a new key just for Keel rather than reusing one another app uses.</li>
            <li>Copy both the <b>API Key</b> and the <b>API Secret</b> (the secret is shown only once) and paste them below.</li>
          </ol>
          <Field label="API Key">
            <Input value={key} autoComplete="off" spellCheck={false} onChange={(e) => setKey(e.target.value)} />
          </Field>
          <Field label="API Secret">
            <Input type="password" value={secret} autoComplete="new-password" spellCheck={false} onChange={(e) => setSecret(e.target.value)} />
          </Field>
          <Text size="xsmall" className="text-ui-fg-subtle">
            Both are stored encrypted and never shown again. Keel checks them with ShipStation before saving and sets up the shipment webhook for you. (A ShipStation API v2 key works too: paste it in API Key and leave API Secret empty.)
          </Text>
        </Drawer.Body>
        <Drawer.Footer>
          <Drawer.Close asChild>
            <Button size="small" variant="secondary">Cancel</Button>
          </Drawer.Close>
          <Button size="small" isLoading={connect.isPending} disabled={key.trim().length < 8} onClick={submit}>
            Connect
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

// ── Order page ──────────────────────────────────────────────────────────────

const ORDER_STATE: Record<NonNullable<OrderShipStation["order"]>["status"] | "on_hold", { label: string; tone: Tone }> = {
  pending: { label: "Waiting to send", tone: "attention" },
  sending: { label: "Sending…", tone: "attention" },
  cancelling: { label: "Cancelling…", tone: "attention" },
  sent: { label: "Sent", tone: "info" },
  partially_shipped: { label: "Partially shipped", tone: "warning" },
  shipped: { label: "Shipped", tone: "success" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  failed: { label: "Failed", tone: "critical" },
  not_needed: { label: "Nothing to ship", tone: "neutral" },
  on_hold: { label: "On hold: payment review", tone: "warning" },
}

/** "ShipStation: sent / shipped / failed (reason) — Retry" on the order page. Hidden when ShipStation isn't connected and never had this order. */
export function OrderShipStationCard({ orderId, canceled }: { orderId: string; canceled: boolean }) {
  const { data } = useOrderShipStation(orderId)
  const resend = useResendToShipStation(orderId)
  const canWrite = useCan()("orders:write")
  const s = data?.shipstation
  if (!s || (!s.integration.connected && !s.order)) return null
  const o = s.order
  const status = o?.status as keyof typeof ORDER_STATE | undefined
  const st = status ? ORDER_STATE[status] ?? { label: String(status), tone: "neutral" as Tone } : null
  const live = s.integration.connected && s.integration.enabled
  const shippedAll = o?.status === "shipped" || o?.status === "partially_shipped"
  const label = !o ? (canceled ? null : "Send to ShipStation") : o.status === "failed" ? "Retry" : o.status === "cancelled" || o.status === "not_needed" || shippedAll ? null : "Resend"
  const send = () => resend.mutate(undefined, { onSuccess: (r) => (r.shipstation.order?.status === "failed" ? toast.error(r.shipstation.order.last_error ?? "ShipStation didn't accept the order") : toast.success("Sent to ShipStation")), onError: (e) => toast.error(errorMessage(e)) })
  return (
    <Section title="ShipStation" actions={st ? <Badge tone={st.tone}>{st.label}</Badge> : <Badge tone="neutral">Not sent</Badge>}>
      <div className="flex flex-col gap-2 px-4 pb-3 text-[13px]">
        {o?.status === "failed" && o.last_error && <Text size="small" className="text-ui-fg-error">{o.last_error}</Text>}
        {o?.status === "failed" && o.next_attempt_at && <Text size="xsmall" className="text-ui-fg-subtle">Tries again automatically {formatDate(o.next_attempt_at)}.</Text>}
        {status === "on_hold" && <Text size="small" className="text-ui-fg-error">{o?.last_error ?? "Not sent: the payment isn't cleared."}</Text>}
        {s.shipments.filter((x) => x.status === "failed").map((x) => (
          <Text key={x.id} size="xsmall" className="text-ui-fg-error">Shipment {x.carrier ?? ""} {x.tracking_number ?? x.id} couldn't be recorded{(x as { last_error?: string | null }).last_error ? `: ${(x as { last_error?: string | null }).last_error}` : ""}. Retried automatically.</Text>
        ))}
        {o?.shipstation_order_id && <Text size="xsmall" className="text-ui-fg-subtle">ShipStation {o.api === "v2" ? "shipment" : "order"} {o.shipstation_order_id}{o.sent_at ? ` · sent ${formatDate(o.sent_at)}` : ""}</Text>}
        {s.shipments.filter((x) => x.status === "done").map((x) => (
          <Text key={x.id} size="xsmall" className="text-ui-fg-subtle">{x.carrier ?? "Shipment"} {x.tracking_number ?? ""} · {formatDate(x.created_at)}</Text>
        ))}
        {!o && !canceled && <Text size="xsmall" className="text-ui-fg-subtle">{live ? "This order hasn't been sent to ShipStation." : "ShipStation is switched off (Settings → Shipping and delivery)."}</Text>}
        {canWrite && live && label && (
          <div>
            <Button size="small" variant="secondary" isLoading={resend.isPending} onClick={send}>{label}</Button>
          </div>
        )}
      </div>
    </Section>
  )
}
